import type { AppointmentTypeId, ClinicId, ProviderId } from "./types";

export type SchedulerView = "day" | "week" | "workWeek" | "month";
export type ColumnGrouping = "provider" | "clinic";
/** Quick sets above the staff list. */
export type StaffPreset = "me" | "team" | "everyone";
/**
 * How the appointment form arranges itself. Two layouts ship together on purpose (Chen,
 * 2026-09-17): `classic` is the old form's field order in a sheet, so nobody is forced
 * to relearn the surface they use 45,000 times a week, and `desk` is the two-pane
 * booking desk. Same fields, same rules, same payload — only the arrangement differs.
 */
export type FormLayout = "classic" | "desk";

/**
 * Per-user scheduler settings. Kickoff decision: these live in the cloud, never in
 * localStorage — exam-room computers are shared. Every key below but one is
 * stored per user in the `scheduler` namespace of the cloud settings store and follow
 * the user to any computer they sign in on (ENG-2047); `slotMinutes` is the exception,
 * because it is the practice's (`Practice.scheduling_cell_duration`).
 *
 * `domain/settings-document.ts` owns what a value has to survive to be stored, and
 * `state/use-settings-sync.hook.ts` owns the traffic. The slice is still where they live
 * while the page is open.
 */
/** Where the event preview sits. See `SchedulerSettings.previewMode`. */
export type PreviewMode = "docked" | "floating";
/** How an appointment card is coloured. See `SchedulerSettings.cardScheme`. */
export type CardScheme = "accent" | "filled";
/** What the form's availability half shows. See `SchedulerSettings.availabilityView`. */
export type AvailabilityView = "nextAvailable" | "staff";
/** Whether provider columns are tinted. See `SchedulerSettings.columnShading`. */
export type ColumnShading = "provider" | "plain";
/** How wide day columns may get before the grid scrolls sideways. See `SchedulerSettings.columnWidth`. */
export type ColumnWidth = "comfortable" | "fit";
/**
 * Whether the form's "Check rooms" lanes start open (ENG-2194, Sam: "should start collapsed
 * or remember"). `remember` reopens them the way this person last left them
 * (`roomLanesLastOpen`). The rule is `initialRoomLanesOpen` in `domain/room-lanes.ts`.
 */
export type RoomLanesPreference = "open" | "closed" | "remember";

export interface SchedulerSettings {
  view: SchedulerView;
  grouping: ColumnGrouping;
  clinicIds: ClinicId[];
  /** Staff whose columns are shown; always a subset of `staffListIds`. */
  providerIds: ProviderId[];
  /**
   * The working staff list in the side panel, in column order. Not everyone in the
   * practice: people are added with search and removed from the list.
   */
  staffListIds: ProviderId[];
  /**
   * The column order the user chose in "Reorder Scheduling Columns" (ENG-2166). Kept apart
   * from `staffListIds` as classic keeps it apart from the staff filter; applied with
   * `orderColumns`. Empty: list order.
   */
  columnOrder: ProviderId[];
  /** The quick set that built the list; `null` once someone is added or removed by hand. */
  staffPreset: StaffPreset | null;
  showBlocks: boolean;
  /** Personal and clinic events (the "Events" chip). */
  showPersonalEvents: boolean;
  showCancelled: boolean;
  /**
   * Unreviewed Draft web bookings. Shown by default: the old scheduler hides them only
   * where LD `web-scheduler-appointments-filter` is on, which in production is the
   * "Suno Audiology" practice alone (read 2026-09-18).
   */
  showWebDrafts: boolean;
  /** Add-staff search: every staff member unless "Providers only" is on. */
  showAllStaff: boolean;
  showInactiveStaff: boolean;
  /** Appointment types drawn; `null` = all (a type added later is included). */
  appointmentTypeIds: AppointmentTypeId[] | null;
  dayStartMinutes: number;
  dayEndMinutes: number;
  /** Grid row size — `Practice.scheduling_cell_duration` in the old grid, default 30. */
  slotMinutes: number;
  /** Which appointment-form layout this user sees. Switchable from the form itself. */
  formLayout: FormLayout;
  /**
   * Where the event preview sits: a rail beside the grid, or a card floating over it.
   *
   * A SETTING rather than transient UI state, because Chen asked for the last choice to
   * stick — drag the card off the rail once and the next one opens floating. It lives here
   * next to `formLayout` rather than in browser storage, which this module bans by lint
   * ("settings live in the schedulerV3 slice"), so it persists through the cloud settings
   * store for free instead of needing a second mechanism.
   */
  previewMode: PreviewMode;
  /**
   * The filter panel folded down to its rail, as the old scheduler's collapse rail does
   * (`scheduler-side-sheet-collapsible.component.tsx:211-239`; its flag serves true to all
   * of production). The old app keeps this in localStorage; here it is a setting, so it
   * follows the user to the next computer rather than to the next person at this one
   * (ENG-2009, stored by ENG-2047).
   */
  sidePanelCollapsed: boolean;
  /**
   * A provider's column also shows the appointments they SUPERVISE, so a supervising
   * audiologist's day does not look emptier than it is. The old scheduler's "Include
   * Supervising Appointment" switch (ENG-2042), whose LD gate `displaySupervisingProvider`
   * serves true to all of production (`on:false` with `offVariation` pointing at the
   * `true` variation, read 2026-09-20), so it ships here ungated.
   *
   * Unlike the old switch this costs no refetch: it sends the backend's
   * `include_supervising_provider` param nowhere, because that param only widens a
   * `staff_member` filter (`scheduling/views/event.py:50-54`) and V3 fetches the whole
   * clinic-day without one. The supervised appointments are already in the cache; only
   * their placement changes.
   */
  includeSupervising: boolean;
  /**
   * NO LONGER READ (ENG-2292). It was ENG-2103's toggle that listed every service location
   * as a chip; a practice can have hundreds, so the side panel now shows them in their own
   * group of a searchable checklist. Kept so stored documents still parse and round-trip.
   */
  showServiceLocations: boolean;
  /**
   * NO LONGER READ (ENG-2194). Kept so stored documents still parse and round-trip; nothing
   * gates on it any more — the card scheme and the form layout are each the person's own
   * pick in the Schedule settings drawer and the form header.
   *
   * Was toggle 2: the opt-in "new booking experience" (ENG-2145, D1 of the 2026-09-23 review:
   * "toggle 2 becomes a setting, not an LD flag"). Toggle 1 is V3 itself, with the classic
   * card and form (ENG-2117); this one unlocks the redesigned looks, starting with the desk
   * form layout. Off, `selectFormLayout` answers `classic` whatever `formLayout` holds, and
   * `formLayout` is kept, so opting back in restores the user's pick.
   */
  newBookingExperience: boolean;
  /**
   * How an appointment card is coloured (ENG-2055, Chen's Silver pick 2026-09-21). `accent`
   * is V3's card: white, with the type colour in a strip (ENG-2117's classic-look card).
   * `filled` is the old scheduler's Classic View: the card takes its type colour, except
   * unconfirmed, cancelled and no-show, which stay white inside a solid border
   * (`appointment-cell.component.tsx:181-184`). Every colour comes from
   * `engine/card-colours.ts`; nothing else reads this.
   *
   * The old switch keeps its answer in localStorage (`scheduler-slice.ts:146,456`). This is
   * a setting instead, so the cloud settings store (ENG-2047) carries it to the next
   * computer with no mechanism of its own.
   */
  cardScheme: CardScheme;
  /**
   * `provider` tints each provider's in-shift hours and header with their colour at 10%,
   * as classic does by default. `plain` leaves columns white, as classic's Classic View does
   * (`use-grouping-styles.hook.ts:56-59`) — the half of Classic View that `cardScheme`
   * never copied (SN-20010, Chen's Silver pick 2026-10-21).
   */
  columnShading: ColumnShading;
  /**
   * `comfortable` (default) keeps day columns at least 240px, so names and types read at the
   * 23 Sep font sizes and the grid scrolls sideways for more people. `fit` lets them go down to
   * classic's 100px, so everyone selected fits as in classic, and the card narrows the way
   * classic's does (`engine/card-width.ts`). Week views keep 150px either way (SN-20011, Chen's
   * Silver pick 2026-10-21).
   */
  columnWidth: ColumnWidth;
  /** Whether the form's room lanes start open. See `RoomLanesPreference`. */
  roomLanes: RoomLanesPreference;
  /** The lanes' last open/closed state, written only while `roomLanes` is `remember`. */
  roomLanesLastOpen: boolean;
  /**
   * What the classic form's availability half shows (SN-20014, Chen 2026-10-21):
   * `nextAvailable` (default) is V3's Next available list above the staff day view;
   * `staff` is the staff day view alone, as classic's form shows it, and the Next available
   * search does not run. St. John's found the list distracting while booking deliberately.
   */
  availabilityView: AvailabilityView;
  /**
   * The Radar rail is open (ENG-2104). A setting like `sidePanelCollapsed`: whether the
   * front desk wants the day's exceptions beside the grid is a preference that survives a
   * trip to a chart and persists through the cloud settings store, shared across clinics.
   */
  radarOpen: boolean;
}

export const DEFAULT_SETTINGS: SchedulerSettings = {
  view: "day",
  grouping: "provider",
  clinicIds: [],
  providerIds: [],
  staffListIds: [],
  columnOrder: [],
  staffPreset: null,
  showBlocks: true,
  showPersonalEvents: true,
  showCancelled: false,
  showWebDrafts: true,
  // The old side sheet's "Provider only" starts unticked.
  showAllStaff: true,
  showInactiveStaff: false,
  appointmentTypeIds: null,
  dayStartMinutes: 7 * 60,
  dayEndMinutes: 19 * 60,
  slotMinutes: 30,
  // Classic by default (ENG-2117, Omar 2026-09-23: "keep the form ~95% the same"): the
  // old form's dialog, cards and order, with Availability on the right. The desk is the
  // future "toggle 2" experience, one click away in the form header.
  formLayout: "classic",
  // ENG-2141: opens beside the clicked appointment; Dock pins it (Chen 2026-09-25).
  previewMode: "floating",
  // Off, like the old switch, which reads `false` out of localStorage. A column that
  // silently gains other people's appointments on first load is the misread this
  // whole feature exists to avoid.
  includeSupervising: false,
  showServiceLocations: false,
  sidePanelCollapsed: false,
  // Opt-in: everyone starts on the classic card and form (ENG-2145).
  newBookingExperience: false,
  // ENG-2194 (Silver): filled — classic's look — is the default. A stored "accent" is kept:
  // the store writes only keys a person touched, so a stored value was clicked, never a
  // default (see the ENG-2194 note in CLAUDE.md).
  cardScheme: "filled",
  // SN-20010: V3's tinted columns stay the default; `plain` is the per-person classic option.
  columnShading: "provider",
  // SN-20011: V3's width stays the default; "fit" is the per-person classic option.
  columnWidth: "comfortable",
  // Chen, 30 Sep (ENG-2194): remember the last choice. `roomLanesLastOpen` starts false, so
  // a first-ever form still opens collapsed, as Sam asked. A stored "closed" is kept.
  roomLanes: "remember",
  roomLanesLastOpen: false,
  // SN-20014: V3's list stays the default; "staff" is the per-person classic view.
  availabilityView: "nextAvailable",
  // Closed until asked for: a rail that opens itself takes a provider column at 1440.
  radarOpen: false
};
